<template>
  <div class="p-2 advanced-table-wrapper">
    <div class="table-area">
      <div class="actions-row">
        <div class="pagination flex items-center gap-2">
          <Pagination
            v-if="table.getPageCount() > 1"
            :pageCount="table.getPageCount()"
            :pageRange="5"
            :clickHandler="paginationClickHandler"
            :firstLastButton="true"
            containerClass="button-group"
            prevLinkClass="border left rounded p-1"
            pageLinkClass="border rounded p-1"
            nextLinkClass="border right rounded p-1"
            :marginPages="0"
            :invalidPageNumbers="getInvalidPageNumbers()"
            :value="table.getState().pagination.pageIndex"
          >
          </Pagination>
          <div class="per-page-wrapper" v-if="table.getRowCount() > 25">
            <span class="per-page-text">Her Sayfada</span>
            <SimpleSelect 
              :simpleSelectItems="pageSizes" 
              :selectedKey="table.getState().pagination.pageSize"
              openMenuPosition="top-left"
              @btnItemClick="sizerClickHandler(Number($event))" 
            />
          </div>
        </div>
      </div>
      <div class="table-wrapper-outer" v-if="getRowsCount">
        <div v-if="loading" class="table-loading-overlay">
          <span class="table-loading-spinner" />
        </div>
        <div id="catalog-table-wrapper" class="table-wrapper table-scroll" :style="[{ ...columnSizeVars() }]">
          <table class="advanced-table">
            <thead>
              <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
                <th
                  v-for="header in headerGroup.headers"
                  :key="header.id"
                  :class="[{ pinned: header.column.getIsPinned() }]"
                  :colSpan="header.colSpan"
                  :style="[{ ...getCommonPinningStyles(header) }]"
                >
                  <FlexRender
                    v-if="!header.isPlaceholder"
                    :render="header.column.columnDef.header"
                    :props="header.getContext()"
                  />
                  <div
                    v-if="header.column.columnDef.enableResizing"
                    class="resizer"
                    :class="[{ 'is-resizing': header.column.getIsResizing() }]"
                    @dblclick="() => header.column.resetSize()"
                    @mousedown="header.getResizeHandler()?.($event)"
                    @touchstart="header.getResizeHandler()?.($event)"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in visibleRows"
                :key="row.id"
                :class="[{ selected: row.getIsSelected() }, { disabled: !row.getCanSelect() }]"
              >
                <template v-for="cell in row.getVisibleCells()" :key="cell.id">
                  <td
                    v-if="shouldRenderCell(row, cell)"
                    :style="{ ...getCommonPinningStyles(cell) }"
                    :rowSpan="getCellRowSpan(row, cell)"
                  >
                    <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="pagination bottom-pagination">
        <Pagination
          v-if="table.getPageCount() > 1"
          :pageCount="table.getPageCount()"
          :pageRange="5"
          :clickHandler="paginationClickHandler"
          :firstLastButton="true"
          containerClass="button-group"
          prevLinkClass="border left rounded p-1"
          pageLinkClass="border rounded p-1"
          nextLinkClass="border right rounded p-1"
          :marginPages="0"
          :invalidPageNumbers="getInvalidPageNumbers()"
          :value="table.getState().pagination.pageIndex"
        >
        </Pagination>
        <div class="per-page-wrapper" v-if="table.getRowCount() > 25">
          <span class="per-page-text">Her Sayfada</span>
          <SimpleSelect 
            :simpleSelectItems="pageSizes" 
            :selectedKey="table.getState().pagination.pageSize"
            openMenuPosition="top-left"
            @btnItemClick="sizerClickHandler(Number($event))" 
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  FlexRender,
  getCoreRowModel,
  useVueTable,
  getExpandedRowModel,
  createColumnHelper,
  getSortedRowModel,
  getPaginationRowModel
} from '@tanstack/vue-table'
import { SimpleSelect } from '@n11/so-ui-components'
import { ref, computed, h, watch } from 'vue'
import Label from "@/components/DataTable/Label"
import Pagination from './Pagination'
import useService from './useService';

const props = defineProps({
  columns: {
    type: Array,
    required: true
  },
  attributes: {
    type: Array,
    required: true
  },
  variants: {
    type: Array,
    required: true
  },
  selectedCurrency: {
    type: Object,
    required: true,
    default: () => ({})
  },
  pinnedColumns: {
    type: Object,
    required: false,
    default: null
  },
  totalCount: {
    type: Number,
    required: false,
    default: 0
  },
  loading: {
    type: Boolean,
    required: false,
    default: false
  },
  listPagination: {
    type: Object,
    required: false,
    default: null
  }
})

const emit = defineEmits(['paginationChange'])

const table = ref(null)
const data = ref([])
const rawData = ref([])
const boostedGroup = ref(null)
const INITIAL_PAGE_INDEX = 0
const INITIAL_PAGE_SIZE = 25

const pageSizes = {
  25: '25 Ürün',
  50: '50 Ürün',
  100: '100 Ürün'
}

const DEFAULT_COLUMN_PINNING = {
  left: ['select-col', 'name', 'images'],
  right: ['vatRate', 'actions']
}

const pagination = ref({
  pageIndex: INITIAL_PAGE_INDEX,
  pageSize: INITIAL_PAGE_SIZE
})

watch(
  () =>
    props.totalCount > 0 && props.listPagination
      ? [props.listPagination.page, props.listPagination.size]
      : null,
  (tuple) => {
    if (!tuple) return
    const [page, size] = tuple
    if (typeof page !== 'number' || typeof size !== 'number') return
    if (pagination.value.pageIndex === page && pagination.value.pageSize === size) return
    pagination.value = { pageIndex: page, pageSize: size }
  },
  { immediate: true }
)

const externalStates = ref({})

const columnPinning = computed(() => {
  return props.pinnedColumns ?? DEFAULT_COLUMN_PINNING
})

const rawPayload = ref({ ...props.payload });
const { nextCursor, prevCursor } = useService(
  pagination,
  rawPayload,
);
const compareOrder = (a, b) => {
  const oa = String(a?.order ?? '')
  const ob = String(b?.order ?? '')
  const splitLast = (s) => {
    const i = s.lastIndexOf('-')
    return i === -1 ? [s, ''] : [s.slice(0, i), s.slice(i + 1)]
  }
  const [pa, sa] = splitLast(oa)
  const [pb, sb] = splitLast(ob)

  if (boostedGroup.value) {
    const aBoost = pa === boostedGroup.value
    const bBoost = pb === boostedGroup.value
    if (aBoost !== bBoost) return aBoost ? -1 : 1
  }

  const byGroup = String(pa).localeCompare(String(pb))
  if (byGroup !== 0) return byGroup

  const na = Number.parseInt(sa, 10)
  const nb = Number.parseInt(sb, 10)
  if (Number.isFinite(na) && Number.isFinite(nb)) return nb - na

  return String(sa).localeCompare(String(sb))
}
const columnHelper = createColumnHelper()

const updateValues = (values, patches = []) => {
  const { pageIndex, pageSize } = pagination.value

  const patchMap = new Map((patches || []).map(r => [r.id, r]))
  const merged = (values || []).map(v => (patchMap.has(v.id) ? { ...v, ...patchMap.get(v.id) } : v))

  const sorted = [...merged].sort(compareOrder)


  rawData.value = sorted

  const start = props.totalCount > 0 ? 0 : pageIndex * pageSize
  const end = props.totalCount > 0 ? sorted.length : Math.min(start + pageSize, sorted.length)
  const page = sorted.slice(start, end).map(row => ({
    ...row,
    attributes: (row.attributes || []).map(a => {
      const normalizedName = a.name || a.attributeName;
      const normalizedValue = a.value ?? a.attributeValue; // null ise attributeValue'yu al
      return {
        ...a,
        name: normalizedName,
        value: normalizedValue,
        pRowSpan: 1
      }
    })
  }))

  const isSlicerByName = new Map(
    (props.attributes || []).map(a => [a.attributeName, !!a.isSlicer])
  )
  const attrNames = new Set(page.flatMap(r => (r.attributes || []).map(a => a.name)))
  const keyOf = (val) => (val == null ? 'null' : (typeof val === 'object' ? JSON.stringify(val) : String(val)))

  for (const name of attrNames) {
    if (!isSlicerByName.get(name)) continue
    let i = 0
    while (i < page.length) {
      const getAttr = (row) => (row.attributes || []).find(a => a.name === name)
      const currentAttr = getAttr(page[i])
      
      const rawValue = currentAttr?.value ?? currentAttr?.attributeValue
      const baseKey = keyOf(rawValue)

      if (rawValue === null || rawValue === undefined || rawValue === '') {
        if (currentAttr) {
          currentAttr.pRowSpan = 1
        }
        i++
        continue
      }

      let j = i
      while (j < page.length && keyOf(getAttr(page[j])?.value ?? getAttr(page[j])?.attributeValue) === baseKey) {
        j++
      }
      
      const span = j - i
      for (let k = i; k < j; k++) {
        const idx = page[k].attributes.findIndex(a => a.name === name)
        if (idx !== -1) {
          page[k].attributes[idx].pRowSpan = (k === i) ? span : 0
        }
      }
      i = j
    }
  }

  for (const row of page) {
    let set = false
    for (const attr of row.attributes || []) {
      if (isSlicerByName.get(attr.name)) {
        row.imagesRowSpan = typeof attr.pRowSpan === 'number' ? attr.pRowSpan : 1
        set = true
        break
      }
    }
    if (!set) row.imagesRowSpan = 1
  }

  return page
}

watch(() => props.variants, (newList) => {
  const prevMap = new Map((rawData.value || []).map(r => [r.id, r]))
  const merged = (newList || []).map(n => {
    const p = prevMap.get(n.id)
    if (!p) return n
    const out = { ...p }
    for (const [k, v] of Object.entries(n)) {
      if (v !== null && v !== undefined) out[k] = v
    }
    if ('attributes' in n) out.attributes = n.attributes
    if (p.validations || n.validations) {
      out.validations = { ...(p.validations || {}), ...(n.validations || {}) }
    }
    return out
  })

  const updatedData = updateValues(merged, data.value) || []
  if (JSON.stringify(data.value) !== JSON.stringify(updatedData)) {
    data.value = updatedData
  }

  const isSingleRowNoAttributes = updatedData.length === 1 && updatedData[0].attributes.length === 0
  const newExternalState = isSingleRowNoAttributes
    ? {
        columnVisibility: { actions: false },
        columnPinning: { left: columnPinning.value.left, right: [] }
      }
    : { columnPinning: columnPinning.value }
  if (JSON.stringify(externalStates.value) !== JSON.stringify(newExternalState)) {
    externalStates.value = newExternalState
  }
}, { immediate: true })

const paginationClickHandler = (pageNum) => {
  const { pageSize } = pagination.value;

  if (props.totalCount > 0) {
    const totalPages = Math.ceil(props.totalCount / pageSize);
    if (totalPages === 0) return;
    const lastIndex = totalPages - 1;
    const clamped = Math.min(Math.max(pageNum, 0), lastIndex);
    if (clamped === pagination.value.pageIndex) return;
    pagination.value.pageIndex = clamped;
    emit('paginationChange', { page: clamped, size: pageSize });
    return;
  }

  const start = pageNum * pageSize;
  const hasDataForPage = rawData.value.slice(start, start + pageSize).length > 0;

  if (!hasDataForPage && pageNum > 0) {
    return;
  }

  table.value.setPageIndex(pageNum);
  pagination.value.pageIndex = pageNum;
  data.value = [...updateValues(rawData.value)];
};

const getInvalidPageNumbers = () => {
  const data = rawData.value;
  const { pageSize } = pagination.value;

  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  if (typeof pageSize !== 'number' || pageSize <= 0) {
    console.error("Page size must be a positive number.");
    return [];
  }

  const invalidPages = new Set();

  data.forEach((item, index) => {
    let itemIsInvalid = false;
    if (item.validations && typeof item.validations === 'object') {
      for (const v of Object.values(item.validations)) {
        if (v && typeof v === 'object' && v.error === true) {
          itemIsInvalid = true;
          break;
        }
      }
    }
    const pageNumber = Math.floor(index / pageSize) + 1;
    if (itemIsInvalid) {
      invalidPages.add(pageNumber);
    }
  });
 
  return Array.from(invalidPages).sort((a, b) => a - b);
}

const sizerClickHandler = (pageNum) => {
  if (props.totalCount > 0) {
    pagination.value.pageSize = pageNum
    pagination.value.pageIndex = 0
    emit('paginationChange', { page: 0, size: pageNum });
    return;
  }

  table.value.setPageSize(pageNum)
  pagination.value.pageSize = pageNum
  data.value = [...updateValues(rawData.value)]
}

const visibleRows = computed(() => {
  if (table.value) {
    return table.value.getRowModel().rows || []
  }
  return []
})

const getRowsCount = computed(() => data.value?.length)

const getColumns = computed(() => {
  const baseColumns = props.columns
  const currentData = data.value

  const isWithoutVariant = currentData.some(item => item.withoutVariant)
  if (isWithoutVariant) return currentData

  const dynamicColumnNames = (props.attributes || []).map(a => a.attributeName)

  const dynamicColumns = dynamicColumnNames.map(name =>
    columnHelper.accessor(name, {
      header: () => name,
      size: 164,
      noVariantsSize: 0,
      cell: ({ cell, row }) => {
        const accessorKey = cell.column.columnDef.accessorKey
        const attribute = row.original.attributes.find((a) => a.name === accessorKey)
        return h(Label, { content: attribute?.value || '' })
      }
    })
  )

  const sliceIndex = 1;

  const updatedColumns = [
    ...baseColumns.slice(0, sliceIndex),
    ...dynamicColumns,
    ...baseColumns.slice(sliceIndex)
  ]

  return currentData.length ? updatedColumns : []
})

const setPagination = ({ pageIndex, pageSize }) => {
  if (props.totalCount > 0) {
    pagination.value.pageIndex = pageIndex;
    pagination.value.pageSize = pageSize;
    return;
  }

  const prevPageIndex = pagination.value.pageIndex;
  const totalPages = table.value.getPageCount();
  const pageIndexDiff = pageIndex - prevPageIndex;

  if (pageIndex === 0) goFirstPage();
  else if (pageIndex === totalPages - 1) goToLastPage();
  else if (pageIndexDiff === 1) goToNextPage();
  else if (pageIndexDiff === -1) goToPreviousPage();
  else if (Math.abs(pageIndexDiff) > 1) goToJumpPage(pageIndexDiff);

  pagination.value.pageIndex = pageIndex;
  pagination.value.pageSize = pageSize;
};

const updatePayload = ({
  after = null,
  before = null,
  jumpPage = null,
  goLast = false,
}) => {
  rawPayload.value = {
    ...rawPayload.value,
    after,
    before,
    jumpPage,
    goLast,
  };
}

const goToLastPage = () => {
  updatePayload({ goLast: true });
};

const goFirstPage = () => {
  updatePayload({});
};

const goToNextPage = () => {
  updatePayload({ after: nextCursor.value });
};

const goToPreviousPage = () => {
  updatePayload({ before: prevCursor.value });
};

const goToJumpPage = (pageDiff) => {
  updatePayload({
    after: pageDiff > 0 ? nextCursor.value : null,
    before: pageDiff < 0 ? prevCursor.value : null,
    jumpPage: Math.abs(pageDiff),
  });
};

table.value = useVueTable({
  get data() {
    return data.value.length ? data.value : []
  },
  get pageCount() {
    const { pageSize } = pagination.value;
    const total = props.totalCount > 0 ? props.totalCount : (props.variants?.length || 0);
    if (!total) return 0;
    return Math.ceil(total / pageSize);
  },
  get rowCount() {
    return props.totalCount > 0 ? props.totalCount : props.variants.length
  },
  get columns() {
    return getColumns.value
  },
  initialState: {
    columnPinning,
    ...externalStates.value
  },
  get state() {
    return {
      pagination: pagination.value,
      ...externalStates.value
    }
  },
  getRowId: row => row.id,
  getCoreRowModel: getCoreRowModel(),
  getExpandedRowModel: getExpandedRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  columnResizeMode: 'onChange',
  defaultColumn: {
    minSize: 60,
    maxSize: 400,
  },
  manualPagination: true,
  onPaginationChange: (updater) => {
    if (typeof updater === 'function') {
      setPagination(
        updater({
          pageIndex: pagination.value.pageIndex,
          pageSize: pagination.value.pageSize,
        }),
      );
    } else {
      setPagination(updater);
    }
  },
  meta: {
    addRow: (variant) => {
      let oldData = []
      if (data.value) {
        oldData = [...data.value]
      }
      data.value = [variant, ...oldData]
    },
    updateRow: (id, columnId, value) => {
      if (data.value) {
        const updatedData = data.value.map((row) => {
          if (row.id === id) {
            return {
              ...row,
              [columnId]: value
            }
          }
          return row
        })
        data.value = updatedData;

        let targetRow = null
        rawData.value = rawData.value.map((row) => {
          if (row.id === id) {
            const next = {
              ...row,
              [columnId]: value
            }
            targetRow = next
            return next
          }
          return row
        })

        if (columnId === 'images' && targetRow) {
          const isSlicer = (name) =>
            (props.attributes || []).some(a => a.attributeName === name && a.isSlicer)

          const sameGroup = (a, b) => {
            const as = (a.attributes || []).filter(x => isSlicer(x.name))
            for (const ax of as) {
              const bv = (b.attributes || []).find(y => y.name === ax.name)?.value
              const av = ax.value
              if (JSON.stringify(av) !== JSON.stringify(bv)) return false
            }
            return true
          }

          rawData.value = rawData.value.map(r =>
            sameGroup(r, targetRow) ? { ...r, images: value } : r
          )
        }

        data.value = updateValues(rawData.value)
      }
    },
    removeRow: (rowId) => {
      rawData.value = [...rawData.value.filter(item => item.id !== rowId)]
      data.value = [...data.value.filter(item => item.id !== rowId)]
    },
    getColumnDataExceptThis: (columnName, id) => {
      const columnData = data.value.map((row) => {
        return row.id === id ? null : row[columnName]
      })
      return columnData.filter((col) => col !== null)
    },
    getData: () => {
      return data.value
    },
    getRawData: () => {
      return rawData.value
    },
    order: () => {
      data.value = data.value.sort(compareOrder)
      return data.value
    }
  }
})

const getRowSpan = (row, cell) => {
  const key = cell.column.columnDef.accessorKey
  if (key === 'images') {
    return row.original.imagesRowSpan
  }
  return row.original.attributes.find((a) => a.name === key)?.rowSpan
}

const getCellKey = (cell) =>
  cell?.column?.columnDef?.accessorKey ?? cell?.column?.id

const getCommonPinningStyles = (column) => {
  const isPinned = column.column.getIsPinned()
  const isLastLeftPinnedColumn = isPinned === 'left' && column.column.getIsLastColumn('left')
  const isFirstRightPinnedColumn = isPinned === 'right' && column.column.getIsFirstColumn('right')

  return {
    boxShadow: isLastLeftPinnedColumn
      ? '-4px 0 4px -4px gray inset'
      : isFirstRightPinnedColumn
        ? '4px 0 4px -4px gray inset'
        : undefined,
    left: isPinned === 'left' ? `${column.column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.column.getAfter('right')}px` : undefined,
    opacity: isPinned ? 0.95 : 1,
    position: isPinned ? 'sticky' : 'relative',
    width: column.column.getSize(),
    zIndex: isPinned ? 1 : 'unset',
    minWidth: `calc(var(--col-${column.column.id}-size) * 1px)`,
    maxWidth: !isPinned ? `calc(var(--col-${column.column.id}-size) * 1px)` : 'auto',
  }
}

const columnSizeVars = () => {
  const headers = table.value.getFlatHeaders()
  const colSizes = {}

  for (let i = 0; i < headers.length; i++) {
    const header = headers[i]
    colSizes[`--header-${header.id}-size`] = header.getSize()
    colSizes[`--col-${header.column.id}-size`] = header.column.getSize()
  }

  return colSizes
}

const getSelectedRows = () => {
  return table.value.getState().rowSelection
}

const formatValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? `(${value.length} items)` : 'No Data'
  }
  return value || 'N/A'
}

const getSelectedRowIds = () => {
  const sel = (table.value?.getState().rowSelection) || {}
  return Object.keys(sel).filter(k => sel[k]).map(k => String(k))
}

const getData = () => {
  return data.value
}

const getRawData = () => {
  return rawData.value
}

const applyBulkToIds = (ids, updates) => {
  const set = new Set((ids || []).map(String))
  rawData.value = (rawData.value || []).map(r => {
    if (!set.has(String(r.id))) return r
    return { ...r, ...updates }
  })
  data.value = updateValues(rawData.value)
}

const getCellRowSpan = (row, cell) => {
  const key = getCellKey(cell)
  if (key === 'images') {
    return row.original.imagesRowSpan ?? 1
  }
  const attr = row.original.attributes?.find(a => a.name === key)
  return typeof attr?.pRowSpan === 'number' ? attr.pRowSpan : 1
}

const shouldRenderCell = (row, cell) => {
  const key = getCellKey(cell)
  if (key === 'images') {
    return (row.original.imagesRowSpan ?? 1) !== 0
  }
  const attr = row.original.attributes?.find(a => a.name === key)
  return (attr?.pRowSpan ?? 1) !== 0
}

defineExpose({
  table,
  columnSizeVars,
  getData,
  getRawData,
  getCommonPinningStyles,
  getRowSpan,
  getSelectedRows,
  visibleRows,
  sizerClickHandler,
  formatValue,
  getSelectedRowIds,
  applyBulkToIds,
  pageSizes,
  paginationClickHandler,
  getRowsCount,
  getInvalidPageNumbers,
  getCellRowSpan,
  shouldRenderCell,
  boostGroup: (prefix) => { boostedGroup.value = prefix },
  clearBoost: () => { boostedGroup.value = null },
})
</script>
<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.advanced-table-wrapper {
  .pagination {
    align-items: center;
    display: flex;
    gap: 24px;
    justify-content: flex-end;
    width: 100%;

    .per-page-wrapper {
      align-items: center;
      display: flex;
      gap: 8px;
      position: relative;
      z-index: 4;

      .per-page-text {
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        white-space: nowrap;
      }
    }

    &.bottom-pagination {
      margin-top: 24px;
      gap: 24px;
    }
  }
}

.actions-row {
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 16px;
}

.table-wrapper-outer {
  position: relative;
  width: 100%;
  max-width: 1328px;
}

.table-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: all;
  border-radius: 8px;
}

.table-loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba($colorN50, 0.6);
  border-top-color: $colorM50;
  border-radius: 50%;
  animation: tableLoadingSpin 0.7s linear infinite;
}

@keyframes tableLoadingSpin {
  to { transform: rotate(360deg); }
}

.table-wrapper {
  position: relative;
  overflow: scroll auto;
  width: 100%;
  max-width: 1328px;
  max-height: 90dvh;
  border: 1px solid $colorN50;
  border-radius: 8px;
  border-collapse: separate;
  border-spacing: 0;

  .resizer {
    position: absolute;
    top: 0;
    right: 0;
    width: 4px;
    height: 100%;
    background: rgba($colorN50, 0.5);
    cursor: col-resize;
    touch-action: none;
    transition: opacity 0.25s ease-in-out;
    user-select: none;
  }

  .resizer.is-resizing {
    background: $colorD50;
    opacity: 1;
  }

  @media (hover: hover) {
    .resizer {
      opacity: 0;
    }

    *:hover > .resizer {
      opacity: 1;
    }
  }

  .empty-table {
    position: sticky;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 64px 0;

    img {
      max-width: 120px;
      margin-bottom: 24px;
    }

    > span {
      display: block;
      color: $colorN80;
      font-size: 16px;
      letter-spacing: -0.05px;
      line-height: 20px;

      &:first-of-type {
        margin-bottom: 12px;
        font-weight: 700;
      }

      &:last-of-type {
        margin-bottom: 16px;
        font-weight: 600;
      }
    }

    button {
      width: 193px;
      gap: 4px;

      img {
        margin: auto 0;
      }
    }
  }
}

.advanced-table {
  width: 100%;
  border-radius: 8px;
  border-collapse: separate;

  th, td {
    padding: unset !important;
    text-align: unset !important;
  }

  :deep(tbody tr td div > .gtin-action-button) {
    color: $colorM50;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    letter-spacing: -0.05px;
  }

  thead {
    background: $colorD10;
    color: $colorN80;

    tr {
      height: 50px;

      th {
        padding: 0px 8px 0px 12px !important;
        text-align: left;
        vertical-align: middle;
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: -0.05px;
        color: $colorN80;
        background-color: $colorBe20;
        border-bottom: 2px solid $colorN50;
        position: sticky !important;
        top: 0;
        z-index: 2 !important;
        min-width: 120px;

        .buy-box-container { 
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: row;
          gap: 2px;
          width: 100%;
          white-space: nowrap;

          div {
            width: 22px;
            height: 22px;
          }
        }

        &.listPrice {
          z-index: 3 !important;
        }

        &.pinned {
          z-index: 3 !important;
        }

        &:not(:last-child) {
          border-right: 1px solid $colorN50;
        }
      }
    }
  }

  tbody {
    tr {
      td {
        background-color: $colorN00;
        height: 80px;
        vertical-align: middle;
        padding: 16px 12px !important;
        > {
          div {
            margin: auto;
          }

          .text-box {
            display: inline-flex;
            height: 40px;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            position: relative;
            margin: 0 auto;
            font-family: "Open Sans";
            font-size: 13px;
            font-style: normal;
            font-weight: 600;
            line-height: 18px;
            letter-spacing: -0.05px;
            color: $colorN80;

            &.name {
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              max-height: 50px;
            }
          }

          label {
            display: block;
            margin: 0 12px;
          }

          .product-info {
            width: 244px;
          }

          .popover-wrapper {
            padding: 0 12px;
          }

          .button-wrapper,
          .status-wrapper {
            margin: 0 12px;
          }

          .checkbox {
            display: flex;
            justify-content: center;
          }
        }

        &:not(:last-child) {
          border-right: 1px solid $colorN50;
        }
      }

      &.selected {
        td:not([rowSpan]) {
          background-color: $colorM05;
        }
      }

      &:not(:last-child) {
        td {
          border-bottom: 1px solid $colorN50;
        }
      }

      &:last-child {
        td :deep(.tooltip-text) {
          top: 60px !important;
        }
      }
    }
  }
}
</style>
